export const name = "COUNT (Differential Privacy)";
export const summary = `\`DIFFERENTIAL_PRIVACY\`-supported \`COUNT\`.       Signature 1: Gets the differentially-private count of rows in a query with a \`DIFFERENTIAL_PRIVACY\` clause.        Signature 2: Gets the differentially-private count of rows with a non-\`NULL\` expression in a query with a \`DIFFERENTIAL_PRIVACY\` clause.`;
export const reference = `
## COUNT (Differential Privacy)

\`DIFFERENTIAL_PRIVACY\`-supported \`COUNT\`.  
  
 Signature 1: Gets the differentially-private count of rows in a query with a \`DIFFERENTIAL_PRIVACY\` clause.   
  
 Signature 2: Gets the differentially-private count of rows with a non-\`NULL\` expression in a query with a \`DIFFERENTIAL_PRIVACY\` clause.

#### Signature 1

### 

\`\`\`
WITH DIFFERENTIAL_PRIVACY ...
  COUNT(
    *,
    [contribution_bounds_per_group => (lower_bound, upper_bound)]
  )
\`\`\`


### Description

Returns the number of rows in the differentially private \`FROM\` clause. The final result is an aggregation across a privacy unit column.

This function must be used with the \`DIFFERENTIAL_PRIVACY\` clauseand can support the following argument:

- \`contribution_bounds_per_group\`: A named argument with a contribution bound. Performs clamping for each group separately before performing intermediate grouping on the privacy unit column.


### Return type

\`INT64\`


### Examples

The following differentially private query counts the number of requests for each item. This query references a table called \`professors\`.



\`\`\`
-- With noise, using the epsilon parameter.
SELECT
  WITH DIFFERENTIAL_PRIVACY
    OPTIONS(epsilon=10, delta=.01, max_groups_contributed=1, privacy_unit_column=id)
    item,
    COUNT(*, contribution_bounds_per_group=>(0, 100)) times_requested
FROM professors
GROUP BY item;

-- These results will change each time you run the query.
-- Smaller aggregations might be removed.
/*----------+-----------------*
 | item | times_requested |
 +----------+-----------------+
 | pencil | 5 |
 | pen | 2 |
 *----------+-----------------*/
\`\`\`



\`\`\`
-- Without noise, using the epsilon parameter.
-- (this un-noised version is for demonstration only)
SELECT
  WITH DIFFERENTIAL_PRIVACY
    OPTIONS(epsilon=1e20, delta=.01, max_groups_contributed=1, privacy_unit_column=id)
    item,
    COUNT(*, contribution_bounds_per_group=>(0, 100)) times_requested
FROM professors
GROUP BY item;

-- These results will not change when you run the query.
/*----------+-----------------*
 | item | times_requested |
 +----------+-----------------+
 | scissors | 1 |
 | pencil | 4 |
 | pen | 3 |
 *----------+-----------------*/
\`\`\`


### Note: For more information about when and when not to use
noise, see Remove noise.

#### Signature 2



\`\`\`
WITH DIFFERENTIAL_PRIVACY ...
  COUNT(
    expression,
    [contribution_bounds_per_group => (lower_bound, upper_bound)]
  )
\`\`\`


### Description

Returns the number of non-\`NULL\` expression values. The final result is an aggregation across a privacy unit column.

This function must be used with the \`DIFFERENTIAL_PRIVACY\` clauseand can support these arguments:

- \`expression\`: The input expression. This expression can be any numeric input type, such as \`INT64\`.
- \`contribution_bounds_per_group\`: A named argument with a contribution bound. Performs clamping per each group separately before performing intermediate grouping on the privacy unit column.


### Return type

\`INT64\`


### Examples

The following differentially private query counts the number of requests made for each type of item. This query references a table called \`professors\`.



\`\`\`
-- With noise, using the epsilon parameter.
SELECT
  WITH DIFFERENTIAL_PRIVACY
    OPTIONS(epsilon=10, delta=.01, max_groups_contributed=1, privacy_unit_column=id)
    item,
    COUNT(item, contribution_bounds_per_group => (0,100)) times_requested
FROM professors
GROUP BY item;

-- These results will change each time you run the query.
-- Smaller aggregations might be removed.
/*----------+-----------------*
 | item | times_requested |
 +----------+-----------------+
 | pencil | 5 |
 | pen | 2 |
 *----------+-----------------*/
\`\`\`



\`\`\`
-- Without noise, using the epsilon parameter.
-- (this un-noised version is for demonstration only)
SELECT
  WITH DIFFERENTIAL_PRIVACY
    OPTIONS(epsilon=1e20, delta=.01, max_groups_contributed=1, privacy_unit_column=id)
    item,
    COUNT(item, contribution_bounds_per_group => (0,100)) times_requested
FROM professors
GROUP BY item;

-- These results will not change when you run the query.
/*----------+-----------------*
 | item | times_requested |
 +----------+-----------------+
 | scissors | 1 |
 | pencil | 4 |
 | pen | 3 |
 *----------+-----------------*/
\`\`\`


### Note: For more information about when and when not to use
noise, see Remove noise.




source: https://cloud.google.com/bigquery/docs/reference/standard-sql/aggregate-dp-functions
`;
